import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { guestTagChipStyle, tagLabel } from '../lib/guestTags';
import { channelLabel } from '../lib/liveBookingToasts';
import {
  effectiveMealEndMs,
  isOverdueUnfinished,
  stripNoteHtml,
} from '../lib/liveBriefing';
import type { ServiceTimelineReservation } from '../types';
import { Icon } from './shared/Icon';

function stateLabelFor(state: number): string {
  const key = `live:reservation.state.${state}`;
  return i18n.exists(key) ? i18n.t(key) : i18n.t('live:reservation.stateN', { state });
}

function fmtClock(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const d = new Date(ms);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Clock, or date+clock when not today (e.g. booking created earlier). */
function fmtWhen(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const d = new Date(ms);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return fmtClock(ms);
  const dd = d.getDate().toString().padStart(2, '0');
  const mo = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${dd}/${mo} ${fmtClock(ms)}`;
}

function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return i18n.t('live:reservation.mins', { count: mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m
    ? i18n.t('live:reservation.durationHm', { h, m })
    : i18n.t('live:reservation.durationH', { h });
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="rsv-row">
      <span className="rsv-lab">{label}</span>
      <span className="rsv-val">{value}</span>
    </div>
  );
}

export function ReservationFocusDrawer({
  reservation,
  onClose,
}: {
  reservation: ServiceTimelineReservation;
  onClose: () => void;
}) {
  const { t } = useTranslation('live');
  const r = reservation;
  const overdue = isOverdueUnfinished(r);
  // Ops view: past scheduled end reads as Finished/Departed until staff marks it.
  const stateLabel = overdue ? t('reservation.finished') : stateLabelFor(r.state);
  const rawStateLabel = stateLabelFor(r.state);
  const tables = (r.tableNames ?? []).filter(Boolean);
  const guestTags = (r.guestTags ?? []).filter(t => t.name?.trim());
  const guestNote = r.guestNote ? stripNoteHtml(r.guestNote) : '';
  const staffNote = r.staffNote ? stripNoteHtml(r.staffNote) : '';
  const hasNotes = !!(guestNote || staffNote);

  return (
    <>
      <div
        className="scrim"
        onMouseDown={e => {
          e.preventDefault();
          onClose();
        }}
        role="presentation"
      />
      <div
        className="drawer rsv-drawer"
        role="dialog"
        aria-label={t('reservation.details')}
        data-ftue="reservation-drawer"
      >
        <div className="dr-head">
          <div>
            <div className="focus-head-row">
              <h2>
                {t('reservation.nGuests', { count: r.peopleCount })}
              </h2>
              {r.isVip && <span className="rsv-vip-badge">{t('reservation.vip')}</span>}
              {overdue && (
                <span className="rsv-overdue-badge">{t('reservation.needsAttention')}</span>
              )}
            </div>
            <p>
              {r.outletName} · {stateLabel} · {channelLabel(r.channel)}
            </p>
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            aria-label={t('reservation.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="dr-body">
          {overdue && (
            <div className="rsv-overdue-callout" role="status">
              <strong>
                {t('reservation.pastEnd', { time: fmtClock(effectiveMealEndMs(r)) })}
              </strong>
              <p>{t('reservation.stillRecorded', { state: rawStateLabel })}</p>
            </div>
          )}

          <div className="rsv-hero">
            <div className="fs-cell">
              <div className="v">{fmtClock(r.startMs)}</div>
              <div className="l">{t('reservation.starts')}</div>
            </div>
            <div className="fs-cell">
              <div className="v">{fmtClock(r.endMs)}</div>
              <div className="l">{t('reservation.ends')}</div>
            </div>
            <div className="fs-cell">
              <div className="v">{fmtDuration(r.durationMs ?? r.endMs - r.startMs)}</div>
              <div className="l">{t('reservation.duration')}</div>
            </div>
          </div>

          {guestTags.length > 0 && (
            <div className="rsv-section">
              <div className="rsv-sec-lab">{t('reservation.guestTags')}</div>
              <ul className="rsv-guest-tags">
                {guestTags.map(t => (
                  <li
                    key={t.name}
                    className="guest-tag-chip"
                    style={guestTagChipStyle(t.color)}
                  >
                    {tagLabel(t)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasNotes && (
            <div className="rsv-section">
              <div className="rsv-sec-lab">{t('reservation.notes')}</div>
              {guestNote && (
                <div className="rsv-note guest">
                  <span className="rsv-note-kind">{t('reservation.guestNote')}</span>
                  <p>{guestNote}</p>
                </div>
              )}
              {staffNote && (
                <div className="rsv-note staff">
                  <span className="rsv-note-kind">{t('reservation.staffNote')}</span>
                  <p>{staffNote}</p>
                </div>
              )}
            </div>
          )}

          <div className="rsv-section">
            <div className="rsv-sec-lab">{t('reservation.timing')}</div>
            <Row label={t('reservation.created')} value={fmtWhen(r.createdAtMs)} />
            <Row label={t('reservation.seated')} value={fmtWhen(r.seatedAtMs)} />
            <Row label={t('reservation.departed')} value={fmtWhen(r.departedAtMs)} />
            <Row label={t('reservation.lastChanged')} value={fmtWhen(r.changedAtMs)} />
          </div>

          {(tables.length > 0 || r.shiftName) && (
            <div className="rsv-section">
              <div className="rsv-sec-lab">{t('reservation.floor')}</div>
              <Row
                label={t('reservation.tables')}
                value={tables.length ? tables.join(', ') : null}
              />
              <Row label={t('reservation.shift')} value={r.shiftName || null} />
            </div>
          )}

          <div className="rsv-section">
            <div className="rsv-sec-lab">{t('reservation.source')}</div>
            <Row label={t('reservation.channel')} value={channelLabel(r.channel)} />
            <Row label={t('reservation.campaign')} value={r.campaign || null} />
            <Row label={t('reservation.utmSource')} value={r.utmSource || null} />
          </div>

          <p className="dr-note">{t('reservation.opsNote')}</p>
        </div>
      </div>
    </>
  );
}
